/**
 * Recruitment Page
 * 採用情報 - 募集要項、応募フォーム、応募後の流れ
 */

import { useState, FormEvent } from 'react';
import axios from 'axios';

interface FormData {
  full_name: string;
  furigana: string;
  age: string;
  gender: string;
  birth_date: string;
  postal_code: string;
  prefecture: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  employment_type: string;
  motivation: string;
  questions: string;
}

const Recruitment = () => {
  const [formData, setFormData] = useState<FormData>({
    full_name: '',
    furigana: '',
    age: '',
    gender: '',
    birth_date: '',
    postal_code: '',
    prefecture: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    employment_type: '',
    motivation: '',
    questions: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await axios.post(`${apiUrl}/applications`, {
        ...formData,
        age: parseInt(formData.age),
      });

      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'ご応募ありがとうございます。内容を確認の上、ご連絡させていただきます。',
        });
        // フォームをリセット
        setFormData({
          full_name: '',
          furigana: '',
          age: '',
          gender: '',
          birth_date: '',
          postal_code: '',
          prefecture: '',
          city: '',
          address: '',
          phone: '',
          email: '',
          employment_type: '',
          motivation: '',
          questions: '',
        });
        // ページトップにスクロール
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      console.error('Application submission error:', error);
      const errorMessage =
        error.response?.data?.message ||
        'エラーが発生しました。もう一度お試しください。';
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <section className="bg-gradient-to-br from-gray-50 via-white to-gray-50 py-20 border-b border-gray-200">
        <div className="container-custom">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in text-rs-gray-dark">
            採用情報
          </h1>
          <p className="text-xl text-gray-500 animate-slide-up">
            RECRUITMENT
          </p>
        </div>
      </section>

      {/* 募集要項 */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-rs-gray-dark">
            募集<span className="text-rs-orange">要項</span>
          </h2>

          <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl overflow-hidden mb-16">
            {/* Mobile: stacked layout, Desktop: table layout */}
            <div className="divide-y divide-gray-200">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  募集職種
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  通信営業スタッフ
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-start">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  仕事内容
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  家電量販店、催事場、ショップイベントでの通信サービス提案営業
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  応募資格
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  18歳以上 / 学歴不問 / 未経験歓迎
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  雇用形態
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  正社員 / アルバイト / パート / 契約社員
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  給与
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  能力・経験を考慮の上、当社規定により優遇
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  勤務時間
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  10:00〜19:00（休憩1時間）※シフト制
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  休日休暇
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  週休2日制 / 有給休暇 / 夏季休暇 / 年末年始休暇
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="px-4 md:px-8 pt-4 pb-2 md:py-6 font-bold text-gray-700 bg-white md:w-1/3">
                  福利厚生
                </div>
                <div className="px-4 md:px-8 pb-4 pt-0 md:py-6 text-gray-600">
                  社会保険完備 / 交通費支給 / 研修制度 / 昇給・昇格制度
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 応募フォーム */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-rs-gray-dark">
            応募<span className="text-rs-orange">フォーム</span>
          </h2>

          <div className="max-w-3xl mx-auto">
            {/* Success/Error Message */}
            {submitStatus.type && (
              <div
                className={`mb-8 p-6 rounded-lg ${
                  submitStatus.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm">
              {/* お名前 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="山田 太郎"
                />
              </div>

              {/* ふりがな */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  ふりがな <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="furigana"
                  value={formData.furigana}
                  onChange={handleChange}
                  required
                  pattern="[ぁ-んー\s]+"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="やまだ たろう"
                />
              </div>

              {/* 年齢・性別 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    年齢 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    required
                    min="18"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                    placeholder="25"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    性別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  >
                    <option value="">選択してください</option>
                    <option value="男性">男性</option>
                    <option value="女性">女性</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
              </div>

              {/* 生年月日 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  生年月日 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                />
              </div>

              {/* 郵便番号 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  郵便番号
                </label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  pattern="\d{3}-?\d{4}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="123-4567"
                />
              </div>

              {/* 都道府県・市区町村 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    都道府県
                  </label>
                  <input
                    type="text"
                    name="prefecture"
                    value={formData.prefecture}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                    placeholder="東京都"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-bold mb-2">
                    市区町村
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                    placeholder="渋谷区"
                  />
                </div>
              </div>

              {/* 住所 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  住所
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="渋谷1-2-3"
                />
              </div>

              {/* 電話番号 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  pattern="0\d{1,4}-?\d{1,4}-?\d{4}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="090-1234-5678"
                />
              </div>

              {/* メールアドレス */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="example@email.com"
                />
              </div>

              {/* 希望する雇用形態 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  希望する雇用形態 <span className="text-red-500">*</span>
                </label>
                <select
                  name="employment_type"
                  value={formData.employment_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                >
                  <option value="">選択してください</option>
                  <option value="正社員">正社員</option>
                  <option value="アルバイト">アルバイト</option>
                  <option value="パート">パート</option>
                  <option value="契約社員">契約社員</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* 志望動機 */}
              <div className="mb-6">
                <label className="block text-gray-700 font-bold mb-2">
                  志望動機 <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="志望動機をご記入ください（10文字以上）"
                />
              </div>

              {/* 質問・ご要望 */}
              <div className="mb-8">
                <label className="block text-gray-700 font-bold mb-2">
                  質問・ご要望
                </label>
                <textarea
                  name="questions"
                  value={formData.questions}
                  onChange={handleChange}
                  maxLength={2000}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-rs-orange focus:border-transparent"
                  placeholder="ご質問やご要望がございましたらご記入ください。"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '送信中...' : '応募する'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 応募後の流れ */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-rs-gray-dark">
            応募後の<span className="text-rs-orange">流れ</span>
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-rs-orange rounded-full flex items-center justify-center text-white font-black text-2xl">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">応募フォーム送信</h3>
                  <p className="text-gray-600 leading-relaxed">
                    上記フォームより必要事項を入力し、送信してください。
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-rs-orange rounded-full flex items-center justify-center text-white font-black text-2xl">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">書類選考</h3>
                  <p className="text-gray-600 leading-relaxed">
                    ご応募内容を確認させていただきます（通常3営業日以内）。
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-rs-orange rounded-full flex items-center justify-center text-white font-black text-2xl">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">面接</h3>
                  <p className="text-gray-600 leading-relaxed">
                    お電話またはメールにて面接日時をご調整させていただきます。
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 bg-rs-orange rounded-full flex items-center justify-center text-white font-black text-2xl">
                  4
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">内定・入社</h3>
                  <p className="text-gray-600 leading-relaxed">
                    面接後、合否をご連絡いたします。内定後、入社日を調整させていただきます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Recruitment;
